import { useEffect, useState } from 'react';
import { Download, CheckCircle2, Loader2 } from 'lucide-react';
import { downloadItem, isDownloaded, isSupported, removeItem } from '@/lib/offlineCache';

/**
 * A small icon button that downloads / removes an audio item to/from the
 * offline cache. Intentionally minimal — it does not change the surrounding
 * card layout.
 */
export default function DownloadButton({ item, className = '', testIdPrefix = 'dl' }) {
    const [state, setState] = useState('idle'); // idle | downloading | done | error
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isSupported() || !item?.audio_url) return;
        let alive = true;
        isDownloaded(item.audio_url).then((d) => { if (alive) setState(d ? 'done' : 'idle'); });
        return () => { alive = false; };
    }, [item?.audio_url]);

    if (!isSupported() || !item?.audio_url) return null;

    const toggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setError('');
        if (state === 'done') {
            await removeItem(item.audio_url);
            setState('idle');
            return;
        }
        setState('downloading');
        try {
            await downloadItem(item);
            setState('done');
        } catch (err) {
            setError(err.message || 'Download failed');
            setState('idle');
        }
    };

    const label = state === 'done' ? 'Remove offline copy' : 'Save for offline';
    return (
        <button
            onClick={toggle}
            aria-label={label}
            title={error || label}
            data-testid={`${testIdPrefix}-${item.id}`}
            className={`rounded-full w-9 h-9 grid place-items-center transition ${
                state === 'done' ? 'text-forest bg-sage/30' : 'text-forest hover:bg-sage/30'
            } ${className}`}
        >
            {state === 'downloading' ? <Loader2 size={16} className="animate-spin" /> :
             state === 'done' ? <CheckCircle2 size={16} /> :
             <Download size={16} />}
        </button>
    );
}
